import * as React from 'react';
import {
  IZScore,
  IZScoreDetails,
} from '../../../../data/providers/reports/zscoreReportProvider';
import { Entity } from '../../../../data/types';
import { roundValue } from '../../../../utils/math';
import { Column } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { Card } from '../../../shared/card/Card';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { Metric } from '../../../shared/Metric';
import { Notable } from '../../../shared/pdf/Notable';
import { EPdfMode, isPdf, shouldInvert } from '../../../shared/pdf/Pdf';
import { SecondaryText } from '../../../shared/SecondaryText';
import { DataCell, DataColumn, LabelCell } from '../../utils/Table';

interface IProps {
  detail: IZScoreDetails;
  colors: string[];
  pdfMode: EPdfMode;
  entity: Entity;
  populationName: string;
}

export const ZScoreDetail = (props: IProps): React.ReactElement => {
  const populationCount = props.detail?.details?.[0]?.populationCount;
  const populationSd = roundValue(
    props.detail?.details?.[0]?.populationSd,
    props.detail?.details?.[0]?.roundingPrecision
  );
  const populationMean = roundValue(
    props.detail?.details?.[0]?.populationMean,
    props.detail?.details?.[0]?.roundingPrecision
  );
  const units = props.detail?.details?.[0]?.units;
  const scaled = props.detail?.details?.[0]?.scaled;
  return (
    <Notable pdf={isPdf(props.pdfMode)}>
      <Card
        style={{ marginTop: 'var(--grid-gap)' }}
        inverse={shouldInvert(props.pdfMode)}
      >
        <Column>
          <CardHeader surface={24}>
            <CardHeaderLeft>
              <Column>
                <div>{props.detail.testMeta}</div>
                <div>{props.detail.metricLabel}</div>
              </Column>
            </CardHeaderLeft>
            <CardHeaderRight>
              <Column style={{ alignItems: 'flex-end' }}>
                <div>Comparing to: {`${populationCount} Tests`}</div>
                <div>
                  1SD : &plusmn; {populationSd}
                  {units}
                </div>
                <div>
                  Mean: {populationMean}
                  {units}
                </div>
              </Column>
            </CardHeaderRight>
          </CardHeader>
          <DetailHeader scaled={scaled} />
          {props.detail.details.map(
            (detail: IZScore, index: number): React.ReactElement => {
              return (
                <ResultRow
                  key={index}
                  color={props.colors[index]}
                  targetName={props.entity.name}
                  populationName={props.populationName}
                  data={detail}
                  lastRow={index + 1 === props.detail.details.length}
                />
              );
            }
          )}
        </Column>
      </Card>
    </Notable>
  );
};

interface IDetailHeaderProps {
  scaled: boolean;
}
const DetailHeader = (props: IDetailHeaderProps): React.ReactElement => {
  return (
    <Row style={{ height: '100%', flex: 0 }}>
      <DataColumn>
        <LabelCell center>Date</LabelCell>
      </DataColumn>
      <DataColumn>
        <LabelCell center>
          ZScore
          {props.scaled && (
            <SecondaryText
              as='span'
              style={{
                fontSize: '0.8em',
                marginLeft: '5px',
                display: 'inline-block',
              }}
            >
              (Scaled 0-100)
            </SecondaryText>
          )}
        </LabelCell>
      </DataColumn>
      <DataColumn>
        <LabelCell center>Mean</LabelCell>
      </DataColumn>
      <DataColumn>
        <LabelCell center last>
          Difference
        </LabelCell>
      </DataColumn>
    </Row>
  );
};

interface IResultRowProps {
  data: IZScore;
  color: string;
  targetName: string;
  populationName: string;
  lastRow: boolean;
}
const ResultRow = (props: IResultRowProps): React.ReactElement => {
  return (
    <Row style={{ height: '100%' }}>
      <DataColumn>
        <DataCell>
          <div>{props.data.date}</div>

          <SecondaryText>{`(${props.data.targetCount} Tests)`}</SecondaryText>
        </DataCell>
      </DataColumn>

      <DataColumn>
        <DataCell>
          <Metric style={{ alignItems: 'center' }} value={props.data.score} />
        </DataCell>
      </DataColumn>
      <DataColumn>
        <DataCell>
          <Metric
            style={{ alignItems: 'center' }}
            value={roundValue(
              props.data.targetMean,
              props.data.roundingPrecision
            )}
            units={props.data.units}
          />
        </DataCell>
      </DataColumn>
      <DataColumn>
        <DataCell lastInRow>
          <Metric
            style={{ alignItems: 'center' }}
            value={roundValue(
              props.data.difference,
              props.data.roundingPrecision
            )}
            units={props.data.units}
            isSecondary
          />
        </DataCell>
      </DataColumn>
    </Row>
  );
};
